import moment from "moment";
import styled from "styled-components";
import React, { useEffect } from "react";
import { COLOR } from '../../../../helpers/theme';
import Spinner from '../../../../components/Spinner';
import BookingManageRooms from "./BookingManageRooms";
import { useDispatch, useSelector } from "react-redux";
import Card from "../../../../components/layouts/Card";
import { Booking } from "../../../../interfaces/Booking";
import { EnumStatus } from "../../../../interfaces/Enums";
import { RootState } from '../../../../redux/rootReducers';
import XrButtonAv from "../../../../components/XrButtonAv";
import { useLocation, useNavigate } from 'react-router-dom';
import BookingManageInventories from "./BookingManageInventories";
import PageWrapper from "../../../../components/layouts/PageWrapper";
import { DataTableWrapper } from "../../../../components/data/DataTable";
import { BootstrapGrid } from "../../../../components/layouts/BootstrapGrid";
import { selectBookingAsync, updateBookingAsync } from "../../../../redux/reducers/bookings/actions";
import { parseStatus } from "../../../../helpers/fieldHelper";

const BookingManagePage = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  
  const isLoading = useSelector((state: RootState) => state.booking.isLoading);
  const booking = useSelector((state: RootState) => state.booking.selectedBooking);

  const disableChange = !(booking && booking.status === 0) || false;

  const populateData = () => {
    const paths = location.pathname.split("/");
    const bookingId = parseInt(paths[3]);
    if (bookingId && !isNaN(bookingId)) {
      dispatch(selectBookingAsync.request({ id: bookingId }));
    } else {
      navigate(`/building-management/bookings/create`);
    }
  }

  useEffect(() => {
    populateData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  const actionAcceptBooking = (status: number) => {
    if (!booking) return;

    const payload: Booking = {...booking};
    payload.notes = payload.notes ? payload.notes : 'Tidak ada catatan tambahan';
    payload.rooms = payload.rooms?.map(item => {
      const payloadStatus = item.status ? item.status : EnumStatus.REJECTED;
      const payloadDescription = item.description ?? '';
      return {
        ...item,
        status: payloadStatus,
        description: payloadDescription
      }
    });
    payload.inventories = payload.inventories?.map(item => {
      const payloadStatus = item.status ? item.status : EnumStatus.REJECTED;
      const payloadDescription = item.description ? item.description : '';
      return {
        ...item,
        status: payloadStatus,
        description: payloadDescription
      }
    });
    payload.status = 2;
    dispatch(updateBookingAsync.request(payload));
  }
  
  const renderFormLoading = () => {
    return (
      <React.Fragment>
        <div className="formLoading">
          <Spinner size="small" />
          <div className="formLoading--caption">Memuat Data...</div>
        </div>
      </React.Fragment>
    )
  }

  const renderActivityInfo = () => {
    if (isLoading) return renderFormLoading();

    return (
      <DataTableWrapper style={{ margin: '0px' }}>
        <div className="tablewrapper--table">
          <table>
            <tbody>
              <tr>
                <td><b>Status Peminjaman</b></td>
                <td>{parseStatus(booking?.status || -1)}</td>
              </tr>
              <tr>
                <td><b>Deskripsi Kegiatan</b></td>
                <td>{booking?.description}</td>
              </tr>
              <tr>
                <td><b>Estimasi Peserta</b></td>
                <td>{booking?.estimate_participant} orang</td>
              </tr>
              <tr>
                <td><b>Tanggal</b></td>
                <td>{moment(booking?.start_date).format('DD MMM YYYY')}</td>
              </tr>
              <tr>
                <td><b>Jam Mulai</b></td>
                <td>{moment(booking?.start_date).format('HH:MM:SS')}</td>
              </tr>
              <tr>
                <td><b>Jam Selesai</b></td>
                <td>{moment(booking?.end_date).format('HH:MM:SS')}</td>
              </tr>
              <tr>
                <td><b>Catatan Tambahan</b></td>
                <td>{booking?.notes}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </DataTableWrapper>
    )
  }

  const renderIssuerInfo = () => {
    if (isLoading) return renderFormLoading();

    return (
      <DataTableWrapper style={{ margin: '0px' }}>
        <div className="tablewrapper--table">
          <table>
            <tbody>
              <tr>
                <td><b>Nama</b></td>
                <td>{booking?.issuer_name}</td>
              </tr>
              <tr>
                <td><b>Nomor Telepon</b></td>
                <td>{booking?.issuer_phone}</td>
              </tr>
              <tr>
                <td><b>Divisi</b></td>
                <td>{booking?.role_detail?.role_name}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </DataTableWrapper>
    );
  }

  const renderApplicantInfo = () => {
    if (isLoading) return renderFormLoading();

    return (
      <DataTableWrapper style={{ margin: '0px' }}>
        <div className="tablewrapper--table">
          <table>
            <tbody>
              <tr>
                <td><b>Nama</b></td>
                <td>{booking?.user_detail?.name}</td>
              </tr>
              <tr>
                <td><b>Divisi</b></td>
                <td>{`Building Management`}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </DataTableWrapper>
    )
  }

  const renderActionPanel = () => {
    if (isLoading) return renderFormLoading();

    return (
      <React.Fragment>
        <XrButtonAv
          noBlock
          size='large'
          color='primary'
          disabled={isLoading || disableChange}
          isLoading={isLoading}
          caption={`Setujui Peminjaman`}
          onClick={() => actionAcceptBooking(1)}
        />
        &nbsp;
        <XrButtonAv
          noBlock
          size='large'
          color='danger'
          disabled={isLoading || disableChange}
          isLoading={isLoading}
          onClick={() => actionAcceptBooking(2)}
          caption={`Tolak Peminjaman`}
        />
      </React.Fragment>
    );
  }

  return (
    <PageWrapper>
      <BookingManagePageWrapper>
        <BootstrapGrid>
          <div className="row">
            <div className="col-lg-4">
              <Card>
                <div className="card--title">
                  <h2 className="card--title-title">Detail Kegiatan</h2>
                </div>
                <div className="card--body">
                  { renderActivityInfo() }
                </div>
              </Card>
              <Card>
                <div className="card--title">
                  <h2 className="card--title-title">Detail Peminjam</h2>
                </div>
                <div className="card--body">
                  { renderIssuerInfo() }
                </div>
              </Card>
              <Card>
                <div className="card--title">
                  <h2 className="card--title-title">Detail Pemohon</h2>
                </div>
                <div className="card--body">
                  { renderApplicantInfo() }
                </div>
              </Card>
              <Card>
                <div className="card--title">
                  <h2 className="card--title-title">Konfirmasi Peminjaman</h2>
                </div>
                <div className="card--body">
                  <p>Dengan menekan tombol dibawah ini, Anda akan mengirimkan notifikasi kepada pengguna yang mengajukan dan tindakan ini tidak dapat diubah atau dibatalkan. Apabila Anda menolak, pengguna harus membuat pengajuan baru.</p>
                  <div className="formSubmit">
                    { renderActionPanel() }
                  </div>
                </div>
              </Card>
            </div>
            <div className="col-lg-8">
              <Card>
                <div className="card--title">
                  <h2 className="card--title-title">Ruangan</h2>
                </div>
                <div className="card--body">
                  <BookingManageRooms />
                </div>
              </Card>
              <Card>
                <div className="card--title">
                  <h2 className="card--title-title">Inventaris</h2>
                </div>
                <div className="card--body">
                  <BookingManageInventories />
                </div>
              </Card>
            </div>
          </div>
        </BootstrapGrid>
      </BookingManagePageWrapper>
    </PageWrapper>
  );
}

const BookingManagePageWrapper = styled.div`
  width: 100%;
  min-height: 100vh;

  & .formTitle {
    margin-bottom: 10px;
  }

  & .formLoading {
    display: flex;
    align-items: center;
    justify-content: center;
    & > .formLoading--caption {
      font-size: 14px;
      margin-top: -3px;
      margin-left: 10px;
    }
  }

  & .formSuccess {
    & > h3 {
      font-size: 14px;
      font-weight: normal;
      text-align: center;
    }
  }
  
  & .formSubmit {
    margin-top: 20px;
    text-align: center;

    & > p {
      margin-top: 5px;
      & > .formSubmit--update {
        cursor: pointer;
        color: ${COLOR.SECONDARY}
      }
    }
  }
`;

export default BookingManagePage;